// The printed documents of Manager Arendă.
import { contractExpiry, formatDate, toNumber } from './ui';
import { distributionOf, productName, productUnit } from './arendaLogic';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
const roNumber = (value, digits = 0) => (Number(value) || 0).toLocaleString('ro-RO', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Two identical A4 halves, made for a black and white printer: meaning comes from text and lines, never colour.
export const chitantaHtml = (record, types = [], { company = '', details = {}, person = null } = {}) => {
  const now = new Date();
  const printedDate = now.toLocaleDateString('ro-RO');
  const printedTime = now.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  const dist = distributionOf(record);

  // A reprint carries today's date, so it also says when the arenda was really given.
  const given = record.takenTimestamp || record.timestamp;
  const givenDate = given ? new Date(given) : null;
  const givenText = givenDate && givenDate.toLocaleDateString('ro-RO') !== printedDate
    ? `${givenDate.toLocaleDateString('ro-RO')}, ora ${givenDate.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  const lines = Object.keys({ ...dist.surfaces, ...dist.amounts })
    .map((type) => ({ type, surface: toNumber(dist.surfaces[type]), amount: Number(dist.amounts[type]) || 0, value: Number(dist.values[type]) || 0, later: Boolean(dist.delayedPickup?.[type]) }))
    .filter((line) => line.surface > 0 || line.amount > 0);
  const totalValue = lines.reduce((sum, line) => sum + line.value, 0);
  const totalSurface = lines.reduce((sum, line) => sum + line.surface, 0);
  const hasLater = lines.some((line) => line.later);
  const picked = dist.pickedUpBy;
  const tight = lines.length + (picked ? 2 : 0) + (hasLater ? 1 : 0) + (givenText ? 1 : 0) > 7;
  const reference = `${record.year || now.getFullYear()}-${String(record.id || '').split('_').pop().slice(-6) || '000000'}`;

  const companyLines = [
    details.cui && `CUI ${details.cui}`,
    details.jNumber && `Nr. Reg. Com. ${details.jNumber}`,
    details.address,
    details.administratorName && `Administrator: ${details.administratorName}`,
  ].filter(Boolean);

  const rows = lines.map((line) => `
        <tr>
          <td>${escapeHtml(productName(line.type, types))}</td>
          <td class="num">${roNumber(line.surface, 2)} ha</td>
          <td class="num">${roNumber(line.amount, Number.isInteger(line.amount) ? 0 : 2)} ${escapeHtml(productUnit(line.type, types))}</td>
          <td class="num">${roNumber(line.value)} lei</td>
          <td class="status">${line.later ? 'În toamnă' : 'Ridicat'}</td>
        </tr>`).join('');

  const copy = (label) => `
    <section class="receipt ${tight ? 'is-tight' : ''}">
      <header class="head">
        <div class="issuer">
          ${company ? `<strong>${escapeHtml(company)}</strong>` : ''}
          ${companyLines.map((line) => `<span>${escapeHtml(line)}</span>`).join('')}
        </div>
        <div class="title">
          <h1>CHITANȚĂ ARENDĂ</h1>
          <p class="copy">${label}</p>
          <p>Nr. ${escapeHtml(reference)} · Anul ${escapeHtml(record.year || '')}</p>
          <p>Tipărit la ${printedDate}, ora ${printedTime}</p>
        </div>
      </header>

      <dl class="facts">
        <div><dt>Beneficiar</dt><dd>${escapeHtml(record.nume)}</dd></div>
        <div><dt>CNP</dt><dd>${escapeHtml(record.cnp)}</dd></div>
        <div><dt>Suprafață totală</dt><dd>${roNumber(record.totalSuprafata ?? record.hectares, 2)} ha</dd></div>
        ${person?.domiciliat && person.domiciliat !== 'N/A' ? `<div class="wide"><dt>Domiciliu</dt><dd>${escapeHtml(person.domiciliat)}</dd></div>` : ''}
        ${givenText ? `<div><dt>Arenda a fost dată la</dt><dd>${givenText}</dd></div>` : ''}
      </dl>

      <table>
        <thead>
          <tr><th>Produs</th><th class="num">Suprafață</th><th class="num">Cantitate</th><th class="num">Valoare</th><th class="status">Status</th></tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr><td>TOTAL</td><td class="num">${roNumber(totalSurface, 2)} ha</td><td></td><td class="num">${roNumber(totalValue)} lei</td><td></td></tr>
        </tfoot>
      </table>

      ${hasLater ? '<p class="note">Produsele marcate „În toamnă” se ridică în toamnă, pe baza acestei chitanțe.</p>' : ''}
      ${picked ? `<p class="note"><strong>RIDICAT DE:</strong> ${escapeHtml(picked.nume)}, CNP ${escapeHtml(picked.cnp)}. Subsemnatul/a declar că am fost împuternicit/ă să ridic produsele în numele beneficiarului ${escapeHtml(record.nume)} și îmi asum responsabilitatea.</p>` : ''}

      <footer class="signatures">
        <div><span class="line"></span>Semnătura ${picked ? 'persoanei împuternicite' : 'beneficiarului'}</div>
        <div><span class="line"></span>Semnătura responsabilului</div>
      </footer>
    </section>`;

  return `<!DOCTYPE html>
<html lang="ro">
<head>
<meta charset="UTF-8">
<title>Chitanță arendă ${escapeHtml(record.nume)}</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 1.3; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h1, p, dl, dd { margin: 0; }
  .sheet { display: flex; flex-direction: column; width: 100%; max-width: 190mm; margin: 0 auto; }
  .receipt { display: flex; flex-direction: column; height: 135mm; padding: 1mm 0; overflow: hidden; }
  .cut { display: flex; align-items: center; gap: 3mm; height: 7mm; color: #000; font-size: 9px; letter-spacing: 0.04em; }
  .cut::before, .cut::after { content: ''; flex: 1; border-top: 1px dashed #000; }
  .head { display: flex; justify-content: space-between; gap: 8mm; padding-bottom: 2.5mm; border-bottom: 2px solid #000; }
  .issuer { min-width: 0; }
  .issuer strong { display: block; font-size: 13px; }
  .issuer span { display: block; font-size: 10px; }
  .title { flex: none; text-align: right; font-size: 10px; }
  .title h1 { font-size: 17px; font-weight: 700; letter-spacing: 0.03em; }
  .title .copy { margin: 0.5mm 0; font-size: 10.5px; font-weight: 700; }
  .facts { display: grid; grid-template-columns: 2fr 1.1fr 0.9fr; gap: 1.2mm 6mm; margin: 2mm 0; }
  .facts .wide { grid-column: span 2; }
  .facts dt { font-size: 9px; }
  .facts dd { font-size: 12px; font-weight: 700; overflow-wrap: anywhere; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 1.1mm 1.5mm; border-bottom: 1px solid #000; text-align: left; font-size: 10.5px; }
  thead th { border-top: 1px solid #000; border-bottom: 2px solid #000; font-size: 9.5px; }
  tfoot td { border-top: 2px solid #000; border-bottom: 2px solid #000; font-weight: 700; font-size: 11.5px; }
  .num { text-align: right; white-space: nowrap; }
  .status { width: 20mm; text-align: right; white-space: nowrap; }
  .note { margin-top: 1.8mm; font-size: 9.5px; }
  .is-tight th, .is-tight td { padding-top: 0.45mm; padding-bottom: 0.45mm; font-size: 9.5px; }
  .is-tight tfoot td { font-size: 10.5px; }
  .is-tight .note { margin-top: 1mm; font-size: 8.5px; }
  .is-tight .issuer span { font-size: 9px; }
  .is-tight .facts dd { font-size: 10.5px; }
  .is-tight .line { height: 7mm; }
  .signatures { display: flex; justify-content: space-between; gap: 14mm; flex: none; margin-top: auto; padding-top: 1.5mm; font-size: 10px; text-align: center; }
  .signatures div { flex: 1; }
  .line { display: block; height: 9mm; margin-bottom: 1mm; border-bottom: 1px solid #000; }
  @media screen {
    body { padding: 12px; }
    .receipt { height: auto; min-height: 120mm; }
    .signatures { margin-top: 8mm; }
  }
  @media print { .sheet { max-width: none; } }
</style>
</head>
<body>
  <main class="sheet">
    ${copy('EXEMPLAR BENEFICIAR')}
    <div class="cut">Decupați aici</div>
    ${copy('EXEMPLAR EVIDENȚĂ')}
  </main>
</body>
</html>`;
};

export const contractHtml = (person) => {
  const personParcels = person.parcels;
  const groupedByT = {};
  personParcels.forEach((parcel) => {
    const key = `${parcel.locatie || ''}|${Number(parcel.t)}`;
    if (!groupedByT[key]) groupedByT[key] = [];
    groupedByT[key].push(parcel);
  });
  const communes = person.tGroups.map((group) => group.comuna).join(', ');
  const totalSuprafata = personParcels.reduce((sum, parcel) => sum + toNumber(parcel.suprafata), 0);
  const contractDate = new Date(person.dataContract);
  const expirationDate = contractExpiry(person.dataContract) || contractDate;
  const companyName = personParcels[0].firma || 'PEPA AGRO SRL';

      // Create contract content
      return `
<!DOCTYPE html>
<html lang="ro">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contract de Arendă - ${person.nume}</title>
    <style>
        body {
            font-family: 'Times New Roman', serif;
            font-size: 12px;
            line-height: 1.6;
            margin: 20px;
            color: #000;
            background: white;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .contract-title {
            font-size: 14px;
            font-weight: bold;
            text-decoration: underline;
            margin: 25px 0;
            text-align: center;
        }
        .chapter {
            font-weight: bold;
            margin: 20px 0 15px 0;
            text-decoration: underline;
        }
        .article {
            margin: 12px 0;
            text-align: justify;
        }
        .indent {
            margin-left: 25px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
        }
        th, td {
            border: 1px solid #000;
            padding: 6px;
            text-align: center;
        }
        th {
            background-color: #f8f8f8;
            font-weight: bold;
        }
        .signature-section {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
        }
        .signature-box {
            width: 45%;
            text-align: center;
        }
        .no-print {
            margin: 20px 0;
            text-align: center;
        }
        .print-btn, .close-btn {
            padding: 12px 24px;
            font-size: 14px;
            border-radius: 4px;
            cursor: pointer;
            margin: 0 10px;
        }
        .print-btn {
            background: #007bff;
            color: white;
            border: none;
        }
        .close-btn {
            background: #6c757d;
            color: white;
            border: none;
        }
        @media print {
            .no-print { display: none !important; }
        }
    </style>
</head>
<body>
    <div class="no-print">
        <button class="print-btn" onclick="window.print()">📄 Printează Contract</button>
        <button class="close-btn" onclick="window.close()">❌ Închide</button>
    </div>

    <div class="header">
        <p><strong>CONTRACT DE ARENDARE Nr. ${person.numarContract || '___'}</strong></p>
        <p><strong>Data: ${contractDate.toLocaleDateString('ro-RO')}</strong></p>
    </div>

    <div class="contract-title">
        <strong>CONTRACT DE ARENDARE</strong>
    </div>

    <div class="chapter">CAPITOLUL I - Părțile contractante</div>
    
    <div class="article">
        <strong>1.1.</strong> Părțile contractante sunt:<br><br>
        <div class="indent">
            <strong>a) Arendatorul:</strong> ${companyName}, reprezentat prin administrator, (denumit în continuare "arendator").<br><br>
            
            <strong>b) Arendașul:</strong> ${person.nume}, domiciliat în ${person.domiciliat}, născut/ă la data de ${person.dataNasterii}, C.I. seria ${person.seria} nr. ${person.numarulBuletinului}, CNP ${person.cnp}, (denumit în continuare "arendaș").
        </div>
    </div>

    <div class="chapter">CAPITOLUL II - Obiectul contractului</div>
    
    <div class="article">
        <strong>2.1.</strong> Obiectul contractului îl constituie arendarea terenului agricol în suprafață de <strong>${totalSuprafata.toFixed(2)} ha</strong>, situat în ${communes}, aparținând arendașului conform tabelului de mai jos:
    </div>

    <table>
        <thead>
            <tr>
                <th>Categoria de folosință</th>
                <th>Tarla</th>
                <th>Parcela</th>
                <th>Suprafața (ha)</th>
                <th>Numărul Cadastral</th>
            </tr>
        </thead>
        <tbody>
            ${Object.values(groupedByT).map((parcelsInT) => 
              parcelsInT.map(parcel => `
                <tr>
                    <td>Teren agricol</td>
                    <td>T${parcel.t}, ${parcel.locatie || '-'}</td>
                    <td>${parcel.parcela || '-'}</td>
                    <td>${parseFloat(parcel.suprafata).toFixed(2)}</td>
                    <td>${parcel.cadastralNumber || '-'}</td>
                </tr>
              `).join('')
            ).join('')}
        </tbody>
    </table>

    <div class="chapter">CAPITOLUL III - Durata contractului</div>
    
    <div class="article">
        <strong>3.1.</strong> Prezentul contract se încheie pentru o perioadă de 10 ani, începând cu data de ${contractDate.toLocaleDateString('ro-RO')} și se încheie la data de ${expirationDate.toLocaleDateString('ro-RO')}.
    </div>

    <div class="chapter">CAPITOLUL IV - Prețul contractual</div>
    
    <div class="article">
        <strong>4.1.</strong> Prețul contractual se stabilește prin acordul părților, plătit în primul trimestru al fiecărui an contractual prin produse agricole și/sau numerar.
    </div>

    <div class="chapter">CAPITOLUL V - Obligațiile părților</div>
    
    <div class="article">
        <strong>5.1.</strong> Obligațiile arendatorului:<br>
        <div class="indent">
            a) să utilizeze terenul conform destinației și să respecte tehnologiile de cultivare;<br>
            b) să plătească la termen prețul contractual;<br>
            c) să mențină în stare bună terenul arendat;<br>
            d) să restituie terenul la expirarea contractului în starea în care l-a primit.
        </div>
    </div>

    <div class="article">
        <strong>5.2.</strong> Obligațiile arendașului:<br>
        <div class="indent">
            a) să predea terenul în termenul și condițiile stabilite prin contract;<br>
            b) să nu împiedice exercitarea drepturilor arendatorului;<br>
            c) să încaseze la termen prețul contractual.
        </div>
    </div>

    <div class="chapter">CAPITOLUL VI - Clauze finale</div>
    
    <div class="article">
        <strong>6.1.</strong> Prezentul contract reprezintă voinţa părţilor şi se încheie în 3 (trei) exemplare.<br>
        <strong>6.2.</strong> Contractul poate fi prelungit prin acordul părților.
    </div>

    <div class="signature-section">
        <div class="signature-box">
            <strong>ARENDATOR</strong><br>
            ${companyName}<br><br>
            _________________
        </div>
        <div class="signature-box">
            <strong>ARENDAȘ</strong><br>
            ${person.nume}<br><br>
            _________________
        </div>
    </div>

</body>
</html>
      `;
};

const contractCell = (person) => {
  if (!person) return 'N/A';
  const date = person.dataContract && person.dataContract !== 'N/A' ? formatDate(person.dataContract) : '';
  return [person.numarContract, date].filter(Boolean).join(' / ') || 'N/A';
};

export const tableHtml = ({ records, year, company, people, types }) => {
      // Generate document content
      return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Tabel Arendă ${year} - ${company === 'all' ? 'Toate Companiile' : company}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12px;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
        }
        .company-info {
            text-align: center;
            margin-bottom: 20px;
            font-weight: bold;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        th, td {
            border: 1px solid #000;
            padding: 8px;
            text-align: center;
            vertical-align: middle;
        }
        th {
            background-color: #f0f0f0;
            font-weight: bold;
        }
        .text-left {
            text-align: left;
        }
        .signature-section {
            margin-top: 40px;
            text-align: center;
        }
        @media print {
            body { margin: 0; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h2>Comuna MIRCEȘTI, SAT IUGANI</h2>
        <h3>JUDEȚUL IAȘI</h3>
        <h3>${company === 'all' ? 'Toate firmele' : company}</h3>
        <br>
        <h1>TABEL ARENDĂ ${year}-IUGANI-COM-MIRCEȘTI</h1>
    </div>

    <table>
        <thead>
            <tr>
                <th>Nr. crt.</th>
                <th>Nume prenume arendas</th>
                <th>Act de identitate (C.I., B.I.)</th>
                <th>Suprafata (ha)</th>
                <th>Nr.contract Data contract inregistrat</th>
                ${types.map(type => `
                    <th>${type.name} (${type.unitType})</th>
                `).join('')}
                <th>Data/Ora Ridicat</th>
                <th>Semnătura arendaș</th>
            </tr>
        </thead>
        <tbody>
            ${records.map((record, index) => {
              const person = people.find(p => p.cnp === record.cnp);
              const takenDate = record.takenTimestamp ? new Date(record.takenTimestamp).toLocaleString('ro-RO') : '';
              
              return `
                <tr>
                    <td>${index + 1}</td>
                    <td class="text-left">${record.nume || 'N/A'}</td>
                    <td>${record.cnp || 'N/A'}</td>
                    <td><strong>${record.hectares ? Number(record.hectares).toFixed(2) : '0.00'}</strong></td>
                    <td class="text-left">${contractCell(person)}</td>
                    ${types.map(type => {
                      const amount = record.distributionData?.amounts?.[type.id] || record.amounts?.[type.id] || 0;
                      return `<td><strong>${amount}</strong></td>`;
                    }).join('')}
                    <td>${takenDate}</td>
                    <td>${takenDate ? 'Chitanță atașată pentru semnătură' : ''}</td>
                </tr>
              `;
            }).join('')}
        </tbody>
    </table>

    <div class="signature-section">
        <p><strong>CONTABIL</strong></p>
        <p>_________________________</p>
        <p>Data: ${new Date().toLocaleDateString('ro-RO')}</p>
    </div>
</body>
</html>`;
};
